import { memo as Memo, useMemo, useRef, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import { usePathname } from 'next/navigation';

//* HOCs
import withUIContext from 'context/consumerHOC/UIConsumer';
import withDataContext from 'context/consumerHOC/DataConsumer';
import withLanguageContext from 'context/consumerHOC/LanguageConsumer';

//* GSAP
import { gsap } from 'gsap';

//* Components
import Container from 'components/common/Container';
import Icon from 'components/common/Icon';
import Logo from 'components/common/Logo';
import Text from 'components/common/Text';
import RunningText from './RunningText';
import GetInTouch from './GetInTouch';
import FooterMenu from './Menu';
import DistributorsSection from './DistributorsSection';

//* Style
import FooterStyle from './style';

const AppFooter = Memo(({ global, selectedLang, winWidth, translate, config }) => {
	//! Global Data
	const globalData = useMemo(() => global?.[selectedLang], [global, selectedLang]);

	//! Router
	const router = useRouter();
	const pathname = usePathname();

	//! Checking
	const isContactPage = useMemo(() => {
		return pathname.includes(config.contacts.path);
	});

	//! Refs
	const footerRef = useRef();
	const backgroundMarginRef = useRef();
	const circleRef = useRef();
	const mapRef = useRef();
	const runningTextRef = useRef();
	const getInTouchRef = useRef();
	const lastScrollTopRef = useRef(0);

	const marginTopVariables = useRef(['352', '288', '232', '216', '216', '216', '192', '104']);
	const sectionDistanceArr = useRef(['200', '168', '144', '136', '128', '112', '128', '80']); //! Section Distance

	//! Margin Top Variable
	const marginTopVariable = useMemo(() => {
		if (winWidth >= 2560) {
			return marginTopVariables.current[0];
		} else if (winWidth >= 1920) {
			return marginTopVariables.current[1];
		} else if (winWidth >= 1510) {
			return marginTopVariables.current[2];
		} else if (winWidth >= 1440) {
			return marginTopVariables.current[3];
		} else if (winWidth >= 1280) {
			return marginTopVariables.current[4];
		} else if (winWidth >= 1024) {
			return marginTopVariables.current[5];
		} else if (winWidth >= 768) {
			return marginTopVariables.current[6];
		} else {
			return marginTopVariables.current[7];
		}
	}, [winWidth]);

	//! Section Distance Variable
	const sectionDistance = useMemo(() => {
		if (winWidth >= 2560) {
			return sectionDistanceArr.current[0];
		} else if (winWidth >= 1920) {
			return sectionDistanceArr.current[1];
		} else if (winWidth >= 1510) {
			return sectionDistanceArr.current[2];
		} else if (winWidth >= 1440) {
			return sectionDistanceArr.current[3];
		} else if (winWidth >= 1280) {
			return sectionDistanceArr.current[4];
		} else if (winWidth >= 1024) {
			return sectionDistanceArr.current[5];
		} else if (winWidth >= 768) {
			return sectionDistanceArr.current[6];
		} else {
			return sectionDistanceArr.current[7];
		}
	}, [winWidth]);

	//! Checker
	const isDistributor = useMemo(() => {
		return (router.pathname === config.home.path || router.pathname === config.aboutUs.path || router.pathname === config.contacts.path || router.pathname === config.products.path) && globalData?.distributor.countries && globalData?.distributor.countries.length;
	}, [router.pathname, config]);

	useEffect(() => {
		gsap.set(footerRef.current, {
			marginTop: `${router.pathname === config.home.path || router.pathname === config.aboutUs.path || router.pathname === config.contacts.path || router.pathname === config.products.path ? 0 : sectionDistance}px`,
		});
		gsap.set(backgroundMarginRef.current, { marginTop: `${marginTopVariable}px` });
		gsap.set(circleRef.current, { borderRadius: `65%` });
	}, [router, isDistributor, marginTopVariable, sectionDistance, config]);

	const handleScroll = useCallback(() => {
		const footerTop = footerRef.current && footerRef.current.getBoundingClientRect().top;
		const windowInnerHeightPartial = window.innerHeight / 2;
		const scrollValue = window.pageYOffset || document.documentElement.scrollTop;

		if (footerTop >= 0 && footerTop <= windowInnerHeightPartial) {
			const diff = footerTop / windowInnerHeightPartial;
			const borderRadSize = diff * 50 + 15;

			//! Kill
			gsap.killTweensOf([circleRef.current, backgroundMarginRef.current, footerRef.current, mapRef.current, getInTouchRef?.current, runningTextRef?.current]);

			gsap.to(circleRef.current, { duration: 0.5, borderRadius: `${borderRadSize}%` });
			gsap.to(backgroundMarginRef.current, { duration: 1, marginTop: `${marginTopVariable * diff}px` });

			gsap.to(footerRef.current, {
				duration: 1,
				marginTop: isDistributor ? `${(1 - diff) * -marginTopVariable}px` : scrollValue > lastScrollTopRef.current ? `${(1 - diff) * -marginTopVariable}px` : `${sectionDistance * diff}px`,
				autoRound: true,
			});

			if (isDistributor || mapRef.current?.imageRef || getInTouchRef.current) {
				gsap.to(isDistributor ? mapRef.current.imageRef : getInTouchRef?.current, { duration: 1, y: isDistributor ? `${40 * diff}%` : `${7 * diff}%`, autoRound: true });
			}

			if (isDistributor || mapRef.current?.titleRef || runningTextRef.current) {
				gsap.to(isDistributor ? mapRef.current.titleRef : runningTextRef?.current, { duration: 1, y: isDistributor ? `${-30 * diff}%` : `${-10 * diff}%`, autoRound: true });
			}

			if (isDistributor && getInTouchRef.current && runningTextRef.current) {
				gsap.set([getInTouchRef?.current, runningTextRef.current], { clearProps: 'all' });
			}
		}

		lastScrollTopRef.current = scrollValue;
	}, [marginTopVariable, sectionDistance, isDistributor]);

	//! Scroll Event Listener
	useEffect(() => {
		window.addEventListener('scroll', handleScroll);

		return () => {
			window.removeEventListener('scroll', handleScroll);
		};
	}, [handleScroll, marginTopVariable, sectionDistance]);

	//! Footer info section
	const ftInfoSection = useMemo(() => {
		return winWidth >= 768 ? (
			<div className={'fl-wrap'}>
				<div className={`ft-company-wrapper`}>
					<Text
						tag={'p'}
						className={`p p4 notoSans-medium primary-color9 company-name-mobile`}
					>
						© {new Date().getFullYear()} {translate('companyName')}. {translate('allRightsReserved')}.
					</Text>
				</div>

				<div className={'f-logo-wrapper'}>
					<Logo />
				</div>

				<div className={`concept-studio-wrapper`}>
					<Text
						tag={'p'}
						className={`p p4 notoSans-medium primary-color9`}
					>
						{translate('madeBy')}
						&nbsp;
						<a
							target={'_blank'}
							rel={'noopener noreferrer'}
							href='https://conceptstudio.com'
							aria-label={'https://conceptstudio.com'}
							className={`p p4 notoSans-medium primary-color9 underline`}
						>
							Concept Studio.
						</a>
					</Text>
				</div>
			</div>
		) : (
			<div className={`align-center`}>
				<Logo className={`mobile-logo-wrap`} />

				<Text
					tag={'p'}
					className={`p p4 notoSans-medium primary-color9 company-name-mobile`}
				>
					© {new Date().getFullYear()} {translate('companyName')}. {translate('allRightsReserved')}.
				</Text>

				<Text
					tag={'p'}
					className={`p p4 notoSans-medium primary-color9 concept-studio-mobile`}
				>
					{translate('madeBy')}
					&nbsp;
					<a
						target={'_blank'}
						rel={'noopener noreferrer'}
						href={'https://conceptstudio.com/en/'}
						aria-label={'https://conceptstudio.com/en/'}
						className={`p p4 notoSans-medium primary-color9 underline`}
					>
						Concept Studio.
					</a>
				</Text>
			</div>
		);
	}, [winWidth, selectedLang]);

	return (
		<FooterStyle
			ref={footerRef}
			className={`footer overflow-hidden relative ${!isDistributor ? `no-distributor` : ``}`}
		>
			<div>
				{isDistributor ? (
					<DistributorsSection
						ref={mapRef}
						title={globalData?.distributor.title}
						image={globalData?.distributor.image}
						distributors={globalData?.distributor.countries}
					/>
				) : (
					''
				)}

				{isContactPage ? null : globalData?.form_title ? (
					<RunningText
						ref={runningTextRef}
						text={globalData.form_title}
					/>
				) : null}

				{isContactPage ? null : (
					<Container
						row
						className={`form-container`}
					>
						<div className={`col-2 col-t-1 col-ts-12 col-m-12`} />

						<div className={`col-8 col-t-10 col-ts-12 col-m-12`}>
							<GetInTouch ref={getInTouchRef} />
						</div>

						<div className={`col-2 col-t-1 col-ts-12 col-m-12`} />
					</Container>
				)}

				<Container
					row
					className={`f-menu-container`}
				>
					<div className={`col-empty`} />

					<div className={`fm-items-wrap col-12`}>
						<FooterMenu />
					</div>

					<div className={`col-empty`} />
				</Container>

				<Container className={`ft-socials-wrapper`}>
					{globalData?.socials.facebook ? (
						<div className={'icon-wrap'}>
							<a
								href={globalData.socials.facebook}
								aria-label={globalData.socials.facebook}
								rel={'noopener noreferrer'}
								target={'_blank'}
							>
								<Icon
									name={`facebook`}
									className={`p p2`}
								/>
							</a>
						</div>
					) : (
						''
					)}

					{globalData?.socials.instagram ? (
						<div className={'icon-wrap'}>
							<a
								target={'_blank'}
								rel={'noopener noreferrer'}
								href={globalData?.socials.instagram}
								aria-label={globalData?.socials.instagram}
							>
								<Icon
									name={`instagram`}
									className={`p p2`}
								/>
							</a>
						</div>
					) : (
						''
					)}
				</Container>

				<Container className={`ft-bottom-wrapper`}>{ftInfoSection}</Container>
			</div>

			<div
				ref={backgroundMarginRef}
				className={`footer-anim-bg`}
			>
				<div className={`global-wrap`}>
					<div
						ref={circleRef}
						className={`circle-wrapper`}
					/>
				</div>
			</div>

			<div className={`footer-fake-bg`} />
		</FooterStyle>
	);
});

export default withLanguageContext(withUIContext(withDataContext(AppFooter, ['global']), ['winWidth']), ['selectedLang', 'translate']);
